/**
 * ADR-0121 Slice 3 — Import a JSON ruleset.
 *
 * Two input sources (PASTE / UPLOAD), per-keystroke client-side parse +
 * preview, target radio (Add to draft DEFAULT vs Import and publish), a
 * coral-dim confirm row gating the publish-replace CTA, collision
 * disclosure when imported rule_ids clash with the current draft, and an
 * all-or-nothing submit via the existing draft / publish endpoints.
 *
 * The client preview never bypasses the server's `validateRules` — it
 * just shows the same reason codes earlier. On submit the API returns a
 * 400 with a typed reason that this modal projects via `mapReason`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Download,
  Upload,
} from 'lucide-react';

import type { Policy, Rule, SiteId } from '@sill/types';

import { Modal } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { humanizeJsonError } from './helpers.js';
import { mapReason, previewValidateRules, type RuleLike } from './import-errors.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/**
 * Hard cap before any network call. The server's body cap is 1 MiB; the
 * dashboard rejects at 256 KiB (the §3 must-hold 1 math says a maxed
 * 64-rule policy is < 512 KiB; 256 KiB is a comfortable user-facing
 * cap, and anything larger is almost certainly a mis-paste).
 */
const MAX_INPUT_BYTES = 256 * 1024;

export interface PolicyImportModalProps {
  open: boolean;
  onClose: () => void;
  site_id: SiteId | undefined;
  site_domain: string | undefined;
  /**
   * The currently-loaded active policy. Used for the `if_match_policy_id`
   * gate on publish, and to surface the version label on the confirm row.
   */
  active_policy: Policy | null;
  /** The current in-page (possibly-draft) rules. Imports merge into these. */
  current_draft_rules: readonly Rule[];
  /** Persist the merged rules to the server-side draft. */
  saveDraft: (rules: readonly Rule[]) => Promise<void>;
  /**
   * Publish-and-replace path. The modal sends `source: 'import'` so the
   * server writes the `admin_audit_log` row.
   */
  publish: (rules: readonly Rule[]) => Promise<void>;
  /** Whether dry-run is currently on (drives copy on the draft target). */
  dryRunEnabled: boolean;
  /**
   * Called after a successful "Add to draft" so the parent can fire the
   * DRY_RUN_JUMP_TO_RESULTS_EVENT (when dry-run is enabled) and show a
   * shadow-log link in the success toast.
   */
  onImportedToDraft: (rule_count: number) => void;
  /** Called after a successful "Import and publish" — parent reloads state. */
  onImportedAndPublished: (rule_count: number) => void;
}

type SourceMode = 'paste' | 'upload';
type Target = 'draft' | 'publish';
type CollisionStrategy = 'skip' | 'overwrite';

interface ParsedOk {
  ok: true;
  rules: RuleLike[];
  grammar_version: number | undefined;
  has_wrapper: boolean;
}
interface ParsedErr {
  ok: false;
  reason: string;
}
type Parsed = ParsedOk | ParsedErr | { ok: false; reason: 'empty' };

/**
 * Try to read the body shape — either a bare `Rule[]` or the wrapper
 * `{ version?, grammar_version?, rules }`. Anything else surfaces as
 * a clear inline error.
 */
function parseInputText(text: string): Parsed {
  const trimmed = text.trim();
  if (trimmed.length === 0) return { ok: false, reason: 'empty' };
  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch (e) {
    return { ok: false, reason: humanizeJsonError(e, trimmed) };
  }
  let rulesInput: unknown;
  let grammar_version: number | undefined;
  let has_wrapper = false;
  if (Array.isArray(raw)) {
    rulesInput = raw;
  } else if (raw !== null && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (!Array.isArray(obj['rules'])) {
      return {
        ok: false,
        reason: 'Expected a `rules` array (or a `{ rules: […] }` wrapper).',
      };
    }
    rulesInput = obj['rules'];
    if (typeof obj['grammar_version'] === 'number') {
      grammar_version = obj['grammar_version'] as number;
    }
    has_wrapper = true;
  } else {
    return {
      ok: false,
      reason: 'Expected a `rules` array (or a `{ rules: […] }` wrapper).',
    };
  }
  const v = previewValidateRules(rulesInput);
  if (!v.ok) return { ok: false, reason: v.reason };
  return { ok: true, rules: v.rules, grammar_version, has_wrapper };
}

export function PolicyImportModal(props: PolicyImportModalProps): JSX.Element {
  const C = useC();
  const {
    open,
    onClose,
    site_id,
    site_domain,
    active_policy,
    current_draft_rules,
    saveDraft,
    publish,
    dryRunEnabled,
    onImportedToDraft,
    onImportedAndPublished,
  } = props;

  const [sourceMode, setSourceMode] = useState<SourceMode>('paste');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [target, setTarget] = useState<Target>('draft');
  const [collisionStrategy, setCollisionStrategy] = useState<CollisionStrategy>('skip');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitHint, setSubmitHint] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset on open / close.
  useEffect(() => {
    if (!open) return;
    setSourceMode('paste');
    setText('');
    setFileName(null);
    setTarget('draft');
    setCollisionStrategy('skip');
    setConfirmReplace(false);
    setSubmitting(false);
    setSubmitError(null);
    setSubmitHint(null);
  }, [open]);

  const byteLength = useMemo(() => {
    if (typeof TextEncoder === 'undefined') return text.length;
    return new TextEncoder().encode(text).length;
  }, [text]);
  const overSize = byteLength > MAX_INPUT_BYTES;

  const parsed: Parsed = useMemo(() => {
    if (overSize) {
      return {
        ok: false,
        reason: `Input is ${byteLength.toLocaleString()} bytes — over the ${(MAX_INPUT_BYTES / 1024).toFixed(0)} KiB cap.`,
      };
    }
    return parseInputText(text);
  }, [text, overSize, byteLength]);

  const existingIds = useMemo(
    () => new Set(current_draft_rules.map((r) => r.rule_id as unknown as string)),
    [current_draft_rules],
  );

  const collisions = useMemo<string[]>(() => {
    if (!parsed.ok) return [];
    const out: string[] = [];
    for (const r of parsed.rules) {
      if (existingIds.has(r.rule_id)) out.push(r.rule_id);
    }
    return out;
  }, [parsed, existingIds]);

  // Build the merged rules array for the draft target (skip vs overwrite).
  const mergedDraftRules = useMemo<Rule[]>(() => {
    if (!parsed.ok) return [];
    if (collisionStrategy === 'overwrite') {
      const next = current_draft_rules.filter(
        (r) => !parsed.rules.some((p) => p.rule_id === (r.rule_id as unknown as string)),
      );
      let order = next.length + 1;
      for (const r of parsed.rules) {
        next.push({ ...(r as unknown as Rule), order: order++ });
      }
      return next;
    }
    // skip — drop colliding rule_ids from the imported set.
    const next: Rule[] = [...current_draft_rules];
    let order = next.length + 1;
    for (const r of parsed.rules) {
      if (existingIds.has(r.rule_id)) continue;
      next.push({ ...(r as unknown as Rule), order: order++ });
    }
    return next;
  }, [parsed, current_draft_rules, collisionStrategy, existingIds]);

  // Build the publish rules array — full replace (the file becomes the
  // new active policy). Order is renumbered 1..N to keep things tidy.
  const publishRules = useMemo<Rule[]>(() => {
    if (!parsed.ok) return [];
    return parsed.rules.map((r, i) => ({ ...(r as unknown as Rule), order: i + 1 }));
  }, [parsed]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_INPUT_BYTES) {
      setText('');
      setFileName(f.name);
      setSubmitError(`File is ${f.size.toLocaleString()} bytes — over the ${(MAX_INPUT_BYTES / 1024).toFixed(0)} KiB cap.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (): void => {
      setText(typeof reader.result === 'string' ? reader.result : '');
      setFileName(f.name);
      setSubmitError(null);
    };
    reader.onerror = (): void => {
      setSubmitError('Could not read the file.');
    };
    reader.readAsText(f);
  };

  const ruleCount = parsed.ok ? parsed.rules.length : 0;
  const importDisabledCommon = !parsed.ok || submitting || site_id === undefined;
  const draftCtaDisabled = importDisabledCommon;
  const publishCtaDisabled = importDisabledCommon || !confirmReplace;

  const onSubmitDraft = async (): Promise<void> => {
    if (!parsed.ok || site_id === undefined) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitHint(null);
    try {
      await saveDraft(mergedDraftRules);
      onImportedToDraft(parsed.rules.length);
      onClose();
    } catch (e) {
      const { label, hint } = projectError(e);
      setSubmitError(label);
      setSubmitHint(hint ?? null);
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitPublish = async (): Promise<void> => {
    if (!parsed.ok || site_id === undefined || !confirmReplace) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitHint(null);
    try {
      await publish(publishRules);
      onImportedAndPublished(parsed.rules.length);
      onClose();
    } catch (e) {
      const { label, hint } = projectError(e);
      setSubmitError(label);
      setSubmitHint(hint ?? null);
    } finally {
      setSubmitting(false);
    }
  };

  // Render-time computed status row: VALID · N RULES (mint) or INVALID · reason (coral).
  const parseStatus = (() => {
    if (text.trim().length === 0) {
      return { tone: 'neutral' as const, label: 'PASTE OR UPLOAD A POLICY JSON' };
    }
    if (parsed.ok) {
      return {
        tone: 'mint' as const,
        label: `VALID · ${ruleCount} RULE${ruleCount === 1 ? '' : 'S'}`,
      };
    }
    const m = mapReason(parsed.reason);
    return { tone: 'coral' as const, label: `INVALID · ${m.label}`, hint: m.hint };
  })();

  // The current active policy version label for the confirm row.
  const versionLabel = active_policy?.version ?? 'unpublished';
  const domainLabel = site_domain ?? 'this site';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Import rules from JSON"
      subtitle="Paste a policy file or upload one. Default lands in your draft (safe — dry-run lights up). Replace-live is gated explicitly."
      closeOnBackdrop={false}
    >
      <div className="p-6 space-y-5">
        {/* Source segmented control */}
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1.5,
              marginBottom: 6,
            }}
          >
            SOURCE
          </div>
          <div className="flex items-center gap-0" role="tablist" aria-label="Import source">
            {(['paste', 'upload'] as SourceMode[]).map((m, i) => {
              const sel = sourceMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  aria-selected={sel}
                  onClick={() => setSourceMode(m)}
                  className="px-3 py-1.5"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 1,
                    color: sel ? C.text : C.muted,
                    background: sel ? C.surface2 : 'transparent',
                    border: `1px solid ${C.border}`,
                    borderLeftWidth: i === 0 ? 1 : 0,
                    borderRadius: 0,
                    cursor: 'pointer',
                  }}
                >
                  {m === 'paste' ? 'PASTE' : 'UPLOAD .JSON'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Input area */}
        {sourceMode === 'upload' ? (
          <div
            className="p-4 flex items-center gap-3 flex-wrap"
            style={{
              background: C.surface2,
              border: `1px dashed ${C.border}`,
              borderRadius: 2,
            }}
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-2 flex items-center gap-2"
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 500,
                color: C.text,
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                cursor: 'pointer',
              }}
            >
              <Upload size={13} /> Choose .json file
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={onFileChange}
              style={{ display: 'none' }}
              aria-hidden="true"
              tabIndex={-1}
              data-testid="policy-import-file-input"
            />
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.muted,
              }}
            >
              {fileName ?? 'No file selected'}
            </span>
          </div>
        ) : null}

        {(sourceMode === 'paste' || text.length > 0) ? (
          <div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 1.5,
                marginBottom: 6,
              }}
            >
              JSON
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              spellCheck={false}
              rows={10}
              placeholder={'{\n  "grammar_version": 1,\n  "rules": [ … ]\n}'}
              className="w-full px-3 py-2"
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                color: C.text,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                outline: 'none',
                resize: 'vertical',
                lineHeight: 1.5,
              }}
              aria-label="Policy JSON input"
              data-testid="policy-import-textarea"
            />
          </div>
        ) : null}

        {/* Live parse status row */}
        <div
          className="px-3 py-2"
          style={{
            background:
              parseStatus.tone === 'mint'
                ? C.mintDim
                : parseStatus.tone === 'coral'
                  ? C.coralDim
                  : C.surface2,
            border: `1px solid ${
              parseStatus.tone === 'mint'
                ? C.mintFg
                : parseStatus.tone === 'coral'
                  ? C.coralFg
                  : C.border
            }`,
            borderRadius: 2,
          }}
          data-testid="policy-import-parse-status"
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              color:
                parseStatus.tone === 'mint'
                  ? C.mintFg
                  : parseStatus.tone === 'coral'
                    ? C.coralFg
                    : C.muted,
            }}
          >
            {parseStatus.label}
          </div>
          {parseStatus.tone === 'coral' && 'hint' in parseStatus && parseStatus.hint ? (
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 11,
                color: C.text,
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              {parseStatus.hint}
            </div>
          ) : null}
        </div>

        {/* Collision disclosure */}
        {parsed.ok && collisions.length > 0 ? (
          <div
            className="p-3"
            style={{
              background: C.amberDim,
              border: `1px solid ${C.amberFg}`,
              borderRadius: 2,
            }}
            data-testid="policy-import-collisions"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={13} color={C.amberFg} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    color: C.amberFg,
                    letterSpacing: 1.5,
                    marginBottom: 4,
                  }}
                >
                  RULE_ID COLLISIONS · {collisions.length}
                </div>
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.text,
                    lineHeight: 1.5,
                    marginBottom: 8,
                  }}
                >
                  These rule ids already exist in your current draft:{' '}
                  <span style={{ fontFamily: monoFont, fontSize: 11 }}>
                    {collisions.join(', ')}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {(
                    [
                      {
                        v: 'skip' as const,
                        label: 'Skip colliding (default)',
                        hint: 'Imported rules with these ids are dropped; your draft is unchanged for them.',
                      },
                      {
                        v: 'overwrite' as const,
                        label: 'Overwrite colliding',
                        hint: 'The imported rules replace the same-id rules in your draft.',
                      },
                    ]
                  ).map((opt) => (
                    <label
                      key={opt.v}
                      className="flex items-start gap-2"
                      style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}
                    >
                      <input
                        type="radio"
                        name="collision-strategy"
                        value={opt.v}
                        checked={collisionStrategy === opt.v}
                        onChange={() => setCollisionStrategy(opt.v)}
                      />
                      <span>
                        <span style={{ fontWeight: 600 }}>{opt.label}</span>
                        <span style={{ display: 'block', color: C.muted, fontSize: 11 }}>{opt.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Target radio */}
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1.5,
              marginBottom: 8,
            }}
          >
            TARGET
          </div>
          <div className="space-y-2">
            <TargetOption
              C={C}
              v="draft"
              selected={target === 'draft'}
              onSelect={() => setTarget('draft')}
              label="Add to draft"
              defaultPill
              description={
                dryRunEnabled
                  ? 'Imported rules merge into your draft. Dry-run is on — the shadow log will show what these rules would have blocked against live traffic.'
                  : 'Imported rules merge into your draft. Review + Publish from the main view when ready.'
              }
            />
            <TargetOption
              C={C}
              v="publish"
              selected={target === 'publish'}
              onSelect={() => setTarget('publish')}
              label="Import and publish"
              description={`Replace the live policy serving real traffic on ${domainLabel}. Records a 'policy_imported_and_published' admin-audit row.`}
            />
          </div>
        </div>

        {/* Coral-dim confirm row — only when publish is selected */}
        {target === 'publish' ? (
          <div
            className="p-3"
            style={{
              background: C.coralDim,
              border: `1px solid ${C.coralFg}`,
              borderRadius: 2,
            }}
            data-testid="policy-import-publish-confirm-row"
          >
            <label
              className="flex items-start gap-2"
              style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}
            >
              <input
                type="checkbox"
                checked={confirmReplace}
                onChange={(e) => setConfirmReplace(e.target.checked)}
                style={{ marginTop: 3 }}
                data-testid="policy-import-confirm-checkbox"
              />
              <span>
                I understand this replaces the live policy serving real agent traffic on{' '}
                <strong>{domainLabel}</strong>. Current version:{' '}
                <span style={{ fontFamily: monoFont, fontSize: 11 }}>{versionLabel}</span>.
              </span>
            </label>
          </div>
        ) : null}

        {/* Submit error row (server-side) */}
        {submitError !== null ? (
          <div
            className="p-2 flex items-start gap-2"
            style={{ background: C.coralDim, border: `1px solid ${C.coralFg}`, borderRadius: 2 }}
          >
            <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{submitError}</div>
              {submitHint !== null ? (
                <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {submitHint}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 pt-3"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2"
            style={{
              background: 'transparent',
              color: C.text,
              border: `1px solid ${C.border}`,
              fontFamily: sansFont,
              fontWeight: 500,
              fontSize: 13,
              borderRadius: 2,
            }}
          >
            Cancel
          </button>
          {target === 'draft' ? (
            <button
              type="button"
              onClick={() => void onSubmitDraft()}
              disabled={draftCtaDisabled}
              className="flex items-center gap-2 px-4 py-2"
              style={{
                background: draftCtaDisabled ? C.surface2 : C.lime,
                color: draftCtaDisabled ? C.muted : C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
                border: `1px solid ${draftCtaDisabled ? C.border : C.lime}`,
                cursor: draftCtaDisabled ? 'not-allowed' : 'pointer',
              }}
              data-testid="policy-import-cta-draft"
            >
              <Download size={13} />
              {submitting ? 'Importing…' : 'Add to draft'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void onSubmitPublish()}
              disabled={publishCtaDisabled}
              className="flex items-center gap-2 px-4 py-2"
              style={{
                background: publishCtaDisabled ? C.surface2 : C.coral,
                color: publishCtaDisabled ? C.muted : '#FFFFFF',
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
                border: `1px solid ${publishCtaDisabled ? C.border : C.coral}`,
                cursor: publishCtaDisabled ? 'not-allowed' : 'pointer',
              }}
              data-testid="policy-import-cta-publish"
            >
              <CheckCircle2 size={13} />
              {submitting ? 'Publishing…' : 'Replace live policy'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function TargetOption({
  C,
  v: _v,
  selected,
  onSelect,
  label,
  description,
  defaultPill = false,
}: {
  C: ReturnType<typeof useC>;
  v: Target;
  selected: boolean;
  onSelect: () => void;
  label: string;
  description: string;
  defaultPill?: boolean;
}): JSX.Element {
  return (
    <label
      className="flex items-start gap-3 p-3 cursor-pointer"
      style={{
        background: selected ? C.surface2 : 'transparent',
        border: `1px solid ${selected ? C.text : C.border}`,
        borderRadius: 2,
      }}
    >
      <input
        type="radio"
        name="policy-import-target"
        checked={selected}
        onChange={onSelect}
        style={{ marginTop: 4 }}
      />
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2">
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
            {label}
          </span>
          {defaultPill ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.limeFg,
                background: C.limeDim,
                border: `1px solid ${C.limeFg}`,
                padding: '1px 6px',
                borderRadius: 2,
                letterSpacing: 1,
              }}
            >
              DEFAULT
            </span>
          ) : null}
        </span>
        <span
          style={{
            display: 'block',
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            marginTop: 4,
          }}
        >
          {description}
        </span>
      </span>
    </label>
  );
}

/**
 * Project an unknown error from the publish/draft submit into a
 * merchant-facing label + optional hint. Handles ApiError with
 * server_reason → mapReason for the typed 400 reasons.
 */
function projectError(e: unknown): { label: string; hint?: string } {
  // We import ApiError lazily here to avoid widening the public module
  // surface; this is the only place that needs to introspect status +
  // server_reason. The import is static at runtime — TS just narrows
  // through the structural shape.
  type ApiErrorShape = { status: number | null; server_reason?: string; message?: string };
  if (typeof e === 'object' && e !== null && 'status' in e) {
    const ae = e as ApiErrorShape;
    if (ae.status === 409) {
      return {
        label:
          'The live policy changed in another session — reload before publishing so you do not overwrite someone else’s change.',
      };
    }
    if (ae.status === 403) {
      return {
        label: 'You do not have permission to publish on this site.',
        hint: 'Only owner or admin roles can publish a policy.',
      };
    }
    if (ae.status === 400 && typeof ae.server_reason === 'string' && ae.server_reason.length > 0) {
      const mapped = mapReason(ae.server_reason);
      return { label: mapped.label, ...(mapped.hint !== undefined ? { hint: mapped.hint } : {}) };
    }
    if (typeof ae.message === 'string') {
      return { label: ae.message };
    }
  }
  if (e instanceof Error) return { label: e.message };
  return { label: 'Import failed for an unknown reason.' };
}
